import {
    CropCategory,
    CropId,
    CropPurpose,
    CropResidue,
    CropStyleFillPattern,
    GrassCuts,
    GrassManagement,
    RegionId,
} from "./generated/enums";
import { IOmniaSession, RGBColor } from "./types";
import { setDates } from "./utils/date";
import { isObject, isUndefinedOrNull } from "./utils/utils";

export interface ICrop {
    CropId: number;
    Name: string;

    Perennial: boolean;

    SowingDateReqd: boolean;
    SowingDate: Date;

    HarvestDateReqd: boolean;
    HarvestDate: Date;

    DestructionDateReqd: boolean;
    DestructionDate: Date;

    CoverCrop: boolean;
    CoverCropComposition: boolean;

    TargetYieldReqd: boolean;
    TargetYield: number;
    TargetYieldMin: number;
    TargetYieldMax: number;

    Composable: boolean;

    Category?: CropCategory;

    Grass: boolean;
    GrassManagement: boolean;
    GrassNumberCuts: boolean;
    GrassComposition: boolean;

    BiomassLevel: boolean;
    GrowingSeasonLength: boolean;

    StrawMulch: boolean;

    ResidueReqd: boolean;
    Residue: CropResidue;

    NLeafAnalysis: boolean;

    Undersown: boolean;

    Varieties: IVariety[];

    GerminationRate: number;
    TGW: number;

    YieldMapMin: number;
    YieldMapMax: number;
    YieldMapUnit: number;

    SeedRatesModel?: string;

    Style?: CropDefaultStyle;
    DefaultStyle?: CropDefaultStyle;

    Deleted?: boolean;
    Hidden?: boolean;

    Purposes?: CropPurposes;
    ProtectionStatus?: boolean;
    GrowthStage?: boolean;
    CropProtectionPlans?: boolean;

    Imagery?: boolean;
    BusinessPlanning?: boolean;

    /** If this is a winter crop, this property will provide the id of its spring counterpart */
    SpringCropId?: number;

    /** If this is a spring crop, this property will provide the id of its winter counterpart */
    WinterCropId?: number;
}

export interface CropPurposes {
    UserSelection: boolean;
    Purposes: CropPurpose[];
}

export interface IVariety {
    CropVarietyId: number;
    Name: string;

    CropId: number;
    Crop: ICrop;

    Market?: CropPurpose;

    GerminationRate: number;
    TGW: number;

    Style?: CropDefaultStyle;
    DefaultStyle?: CropDefaultStyle;

    Deleted?: boolean;
}

export interface CropManagementOption {
    ManagementOptionId: number;
    Region: RegionId;
    Code: string;
    Text: string;
    Inactive?: boolean;
}

export interface CropDefaultStyle {
    Colour: RGBColor;
    Pattern: CropStyleFillPattern;
}

export interface CropStyle {
    /**
     * The ID of the `Crop` this style applies to. (required)
     */
    CropId: number;
    /**
     * The year this style applies to. (required)
     */
    Year: number;
    /**
     * The color of the style. (required)
     */
    Color: RGBColor;
    /**
     * The pattern of the style (optional).
     */
    Pattern?: CropStyleFillPattern | null;
    /**
     * The ID of the `CropVariety` this style applies to (optional)
     */
    VarietyId?: number | null;
    /**
     * The ID of the `CropPurpose` this style applies to (optional)
     */
    Purpose?: CropPurpose | null;
    /**
     * The `GrassManagement` this style applies to (optional, paired with `GrassCuts`)
     */
    GrassManagement?: GrassManagement | null;
    /**
     * The `GrassCuts` this style applies to (optional, paired with `GrassManagement`)
     */
    GrassCuts?: GrassCuts | null;
    /**
     * The `Cropping.CropDescr` this style applies to (optional)
     */
    CroppingCropDescr?: string | null;
}

export type CroppingStyleMode = "crop" | "variety" | "purposeAndGrass" | "description";

export type CroppingStyle = Record<CroppingStyleMode, CropStyle | null>;

export function isCrop(crop: any): crop is ICrop {
    return crop && isObject(crop) && !isUndefinedOrNull(crop.CropId) && isUndefinedOrNull(crop.CropVarietyId);
}

export function isVariety(vari: any): vari is IVariety {
    return vari && isObject(vari) && !isUndefinedOrNull(vari.CropVarietyId);
}

export function getCropAndVariety(arg: ICrop | IVariety): { crop?: ICrop; variety?: IVariety } {
    if (isCrop(arg)) {
        return {
            crop: arg,
        };
    }

    if (isVariety(arg)) {
        return {
            crop: arg.Crop,
            variety: arg,
        };
    }

    return {};
}

export function isCropHidden(crop: { crop: ICrop; cropId: number }) {
    if (!crop) {
        return false;
    }

    if (crop.crop) {
        return !!crop.crop.Hidden;
    }

    switch (crop.cropId || 0) {
        case CropId.NonFarmedArea:
        case CropId.Woodland:
            return true;

        default:
            return false;
    }
}

export function setupCropsAndVarieties(crops: ICrop[], varieties: IVariety[]) {
    setDates(crops, ["SowingDate", "HarvestDate", "DestructionDate", "StartDate", "EndDate"], true);

    crops.forEach((crop) => (crop.Varieties = varieties.filter((v) => v.CropId == crop.CropId)));

    varieties.forEach((v) => {
        v.Crop = crops.find((c) => c.CropId == v.CropId);
        v.Name = (v.Name || "").trim();
    });
}

export function convertThousandSeedsToKg(rate: number, tgw: number): number {
    return Math.round((rate * tgw) / 1000);
}

export function convertSeedRatesToKg(rate: number, tgw: number, flatRate = false): number {
    rate = (rate * tgw) / 100;

    rate = flatRate || rate < 10 ? Math.round(rate * 10) / 10 : Math.round(rate);

    return rate;
}

export function convertSeedRatesFromKg(rate: number, tgw: number): number {
    return Math.round((rate / tgw) * 100);
}

export function getCropManagementConfig(session: IOmniaSession) {
    const {
        config: { features },
        holdingRegion,
    } = session;

    const enabled = features.fields.cropManagementOptions;

    switch (holdingRegion) {
        case RegionId.England:
            return {
                enabled,
                title: "SFI management options",
                shortTitle: "SFI",
            };

        case RegionId.Scotland:
            return {
                enabled,
                title: "AECS management options",
                shortTitle: "AECS",
            };

        default: {
            return {
                enabled: false,
                title: "Crop management options",
                shortTitle: "Management",
            };
        }
    }
}
